import { memo, useEffect, useState } from "react";
import { CropIcon, FireMark, type CropMark } from "./CropMarks";
import { PanelHeader } from "./PanelHeader";
import type { FallbackTier, GistdaHotspotSnapshot, GistdaFloodSnapshot } from "@kmitl/shared";

/**
 * GISTDA national pulse — sidebar block summarizing Thailand's water + crop
 * status. Why surface this on a campus dashboard:
 *   1. Bangkok's water supply is gated by the Central (ภาคกลาง) reservoir
 *      system. The % of capacity is a leading signal for next-month tariffs
 *      and drought response.
 *   2. Rice straw + sugarcane field burning in the top-ranked provinces are
 *      the dominant northern/central PM2.5 sources during the dry season.
 *      Pairing the AQ panel with "burning is most likely from <province>"
 *      gives the chips meaning instead of just numbers.
 */

interface RegionWater {
  regionCode: string;
  regionName: string;
  capacity: number;
  volume: number;
  amount: number;
  weekRef: string;
}
interface WaterSnapshot { regions: RegionWater[]; provinces: unknown[]; fetchedAt: string }

interface CropProvince { province_name: string; rai: number }
interface CropRanking { crop: "rice" | "cassava" | "maize" | "sugarcane"; items: CropProvince[]; fetchedAt: string }

interface Props {
  apiBase: string;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

// Crop marks are monochrome SVG, not emoji — see CropMarks.tsx for why.
const CROP_MARKS: Record<string, CropMark> = {
  rice: "rice",
  cassava: "cassava",
  maize: "maize",
  sugarcane: "sugarcane",
};

const CROP_LABEL: Record<string, string> = {
  rice: "Rice",
  cassava: "Cassava",
  maize: "Maize",
  sugarcane: "Sugarcane",
};

function pct(volume: number, capacity: number): string {
  if (!capacity) return "—";
  return `${((volume / capacity) * 100).toFixed(1)}%`;
}

function pctClass(volume: number, capacity: number): string {
  if (!capacity) return "neutral";
  const v = (volume / capacity) * 100;
  if (v < 25) return "bad";
  if (v < 40) return "warn";
  if (v < 70) return "ok";
  return "good";
}

const FLOOD_SEVERITY_LABEL: Record<string, string> = {
  watch: "Watch",
  warning: "Warning",
  severe: "Severe",
};

/** GISTDA flood extent severity → house severity ladder.
 *
 *  This used to name Tailwind tokens that do not exist in this project
 *  (`--yellow-400`, `--orange-400`, `--red-500`), so the var() fallbacks
 *  did the work and the panel rendered Tailwind's yellow/amber/red —
 *  three hues from a palette the house does not use, next to an AQI badge
 *  that used a fourth. The ladder in tokens.css is one set of ordered
 *  steps shared by every scale that has an order. */
const FLOOD_SEVERITY_COLOR: Record<string, string> = {
  watch: "var(--flood-watch)",
  warning: "var(--flood-warning)",
  severe: "var(--flood-severe)",
};

export const GistdaPulse = memo(function GistdaPulse({ apiBase, ageMinutes, fallbackTier }: Props) {
  const [water, setWater] = useState<WaterSnapshot | null>(null);
  const [crops, setCrops] = useState<CropRanking[]>([]);
  const [fire, setFire] = useState<GistdaHotspotSnapshot | null>(null);
  const [flood, setFlood] = useState<GistdaFloodSnapshot | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    fetch(`${apiBase}/api/gistda/water`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((j) => setWater((j.features ?? [])[0] ?? null))
      .catch(() => {});
    fetch(`${apiBase}/api/gistda/agriculture`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((j) => setCrops(j.features ?? []))
      .catch(() => {});
    fetch(`${apiBase}/api/gistda/fire`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((j) => setFire((j.features ?? [])[0] ?? null))
      .catch(() => {});
    fetch(`${apiBase}/api/gistda/flood`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((j) => setFlood((j.features ?? [])[0] ?? null))
      .catch(() => {});
    return () => ctrl.abort();
  }, [apiBase]);

  const hotspots = fire?.hotspots ?? [];
  const floodExtents = flood?.extents ?? [];

  if (!water && crops.length === 0 && hotspots.length === 0 && floodExtents.length === 0) return null;

  // Province breakdown for fire hotspots
  const hotspotsByProvince = hotspots.reduce<Record<string, number>>((acc, h) => {
    const p = h.province ?? "Unknown";
    acc[p] = (acc[p] ?? 0) + 1;
    return acc;
  }, {});
  const topProvinces = Object.entries(hotspotsByProvince)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  // Severity-sorted flood extents
  const severityOrder: Record<string, number> = { severe: 0, warning: 1, watch: 2 };
  const sortedExtents = floodExtents
    .slice()
    .sort((a, b) => (severityOrder[a.severity ?? ""] ?? 3) - (severityOrder[b.severity ?? ""] ?? 3))
    .slice(0, 8);

  const central = water?.regions.find((r) => r.regionCode === "Central") ?? null;
  const sortedRegions = (water?.regions ?? [])
    .slice()
    .sort((a, b) => (a.volume / a.capacity) - (b.volume / b.capacity));

  return (
    <div className="gistda-pulse">
      <PanelHeader
        title="NATIONAL CONTEXT"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        source="gistda"
        actions={
          <span className="caption mono">
            {water?.fetchedAt ? `wk ${water.fetchedAt.slice(0, 10)}` : ""}
          </span>
        }
      />
      {central && (
        <div className="gistda-water-lead">
          <div className="gistda-water-headline">
            <span className="gistda-water-pct" data-tier={pctClass(central.volume, central.capacity)}>
              {pct(central.volume, central.capacity)}
            </span>
            <div className="col">
              <strong>Central reservoirs</strong>
              <span className="caption">Bangkok's water region · {central.amount.toLocaleString()} bodies</span>
            </div>
          </div>
        </div>
      )}

      {sortedRegions.length > 0 && (
        <div className="gistda-regions">
          {sortedRegions.map((r) => (
            <div key={r.regionCode} className="gistda-region-row" title={`${r.regionName} · ${r.volume.toLocaleString()} / ${r.capacity.toLocaleString()} m³ · ${r.amount.toLocaleString()} reservoirs`}>
              <span className="gistda-region-name mono">{r.regionCode}</span>
              <span className="gistda-region-bar">
                <span
                  className="gistda-region-bar-fill"
                  data-tier={pctClass(r.volume, r.capacity)}
                  style={{ width: `${Math.min(100, (r.volume / r.capacity) * 100).toFixed(0)}%` }}
                />
              </span>
              <span className="gistda-region-pct mono" data-tier={pctClass(r.volume, r.capacity)}>
                {pct(r.volume, r.capacity)}
              </span>
            </div>
          ))}
        </div>
      )}

      {crops.length > 0 && (
        <div className="gistda-crops">
          <span className="eyebrow mono">Burning-season smoke</span>
          <div className="gistda-crop-rows">
            {crops.map((c) => (
              <div key={c.crop} className="gistda-crop-row" title={`${CROP_LABEL[c.crop]} cultivation (rai). Top provinces are typical burning-season smoke sources.`}>
                <CropIcon mark={CROP_MARKS[c.crop] ?? "rice"} />
                <span className="gistda-crop-name mono">{CROP_LABEL[c.crop]}</span>
                <span className="gistda-crop-top mono">
                  {c.items.slice(0, 3).map((it) => it.province_name).join(" · ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hotspots.length > 0 && (
        <div className="gistda-fire">
          <div className="gistda-fire-header">
            <span className="eyebrow mono">VIIRS active fire hotspots</span>
            <span
              className="gistda-fire-count mono"
              title={`${hotspots.length} hotspot${hotspots.length !== 1 ? "s" : ""} detected by satellite`}
            >
              {hotspots.length} hotspot{hotspots.length !== 1 ? "s" : ""}
            </span>
          </div>
          {topProvinces.length > 0 && (
            <div className="gistda-fire-provinces">
              {topProvinces.map(([province, count]) => (
                <div key={province} className="gistda-fire-province-row">
                  <FireMark />
                  <span className="gistda-fire-province mono">{province}</span>
                  <span className="gistda-fire-pcount mono">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {sortedExtents.length > 0 && (
        <div className="gistda-flood">
          <span className="eyebrow mono">Active flood extents</span>
          <div className="gistda-flood-rows">
            {sortedExtents.map((ext, i) => (
              <div
                key={`${ext.province}-${i}`}
                className="gistda-flood-row"
                title={ext.areaRai != null ? `${ext.areaRai.toLocaleString()} rai flooded` : ""}
              >
                <span
                  className="gistda-flood-severity mono"
                  style={{ color: FLOOD_SEVERITY_COLOR[ext.severity ?? ""] ?? "var(--ink-mid)" }}
                >
                  {FLOOD_SEVERITY_LABEL[ext.severity ?? ""] ?? ext.severity ?? "—"}
                </span>
                <span className="gistda-flood-province mono">
                  {ext.province}{ext.district ? ` · ${ext.district}` : ""}
                </span>
                {ext.areaRai != null && (
                  <span className="gistda-flood-area mono">{ext.areaRai.toLocaleString()} rai</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
